import { makeStyles, tokens } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

export const useProjectDashboardPageStyles = makeStyles({
    page: {
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: primer.canvas,
        color: primer.fg,
        overflowX: "hidden",
    },
    body: { flex: "1", display: "flex", alignItems: "flex-start", minHeight: 0, overflowY: "auto" as const, overflowX: "hidden" as const, gap: "8px" },
    emptyBody: { flex: "1", display: "flex", minHeight: 0, overflowY: "auto" as const },
    emptyMainContent: {
        flex: "1",
        minWidth: 0,
        padding: "20px 24px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    mainContent: {
        flex: "1",
        minWidth: 0,
        overflow: "visible",
        padding: "20px 24px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
    },
    titleText: {
        fontSize: "24px",
        fontWeight: 700,
        color: primer.fg,
        whiteSpace: "nowrap" as const,
    },
    titleSpacer: { flex: "1" },
    refreshBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 12px",
        fontSize: "13px",
        fontWeight: 500,
        color: primer.fg,
        backgroundColor: primer.surface,
        border: `1px solid ${primer.border}`,
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap" as const,
        "&:hover": { backgroundColor: primer.overlay },
    },
    openAppBtn: {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: 500,
        color: "#ffffff",
        backgroundColor: "#238636",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap" as const,
        textDecoration: "none",
        "&:hover": { backgroundColor: "#2ea043" },
    },
    viewingDetails: {
        fontSize: "13px",
        fontWeight: 500,
        color: primer.fgMuted,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    tableArea: {},
    sidebar: {
        width: "320px",
        flexShrink: 0,
        padding: "20px 20px 20px 0",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        overflow: "visible",
    },
    center: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: primer.fgMuted,
        fontSize: "14px",
    },
    videoOverlay: {
        position: "fixed" as const,
        inset: "0",
        backgroundColor: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
    },
    videoModal: {
        backgroundColor: primer.surface,
        borderRadius: "8px",
        border: `1px solid ${primer.border}`,
        width: "720px",
        maxHeight: "80vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    videoHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 16px",
        borderBottom: `1px solid ${primer.border}`,
    },
    videoTitle: { fontSize: "14px", fontWeight: 600, color: primer.fg },
    videoClose: {
        border: "none",
        background: "none",
        color: primer.fgMuted,
        fontSize: "18px",
        cursor: "pointer",
    },
    videoPlayer: { width: "100%", display: "block" },
});

export const useProjectDashboardStyles = makeStyles({
    container: {
        height: "100%",
        width: "100%",
        backgroundColor: tokens.colorNeutralBackground1,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "auto",
    },
    content: {
        flex: "1 1 auto",
        overflow: "visible",
        padding: "20px 24px 24px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        minHeight: "300px",
    },
    loadingText: {
        fontSize: "14px",
        color: tokens.colorNeutralForeground2,
    },
    errorContainer: {
        padding: "20px",
        color: tokens.colorPaletteRedForeground1,
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    },
    emptyContainer: {
        padding: "40px 20px",
        textAlign: "center",
        color: tokens.colorNeutralForeground2,
    },
    detailsSection: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    filterToolbar: {
        display: "flex",
        gap: "12px",
        alignItems: "center" as const,
        flexWrap: "wrap" as const,
    },
    filterSearchBox: {
        flex: "0 0 468px",
        height: "32px",
        padding: "0 10px",
        fontSize: "14px",
        borderRadius: "4px",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderBottom: "1px solid #616161",
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        outline: "none",
        ":focus": { borderColor: tokens.colorBrandStroke1 as unknown as never },
    },
    filterChipsContainer: {
        display: "flex",
        gap: "6px",
        flexWrap: "wrap" as const,
        alignItems: "center" as const,
    },
    filterChip: {
        display: "inline-flex",
        alignItems: "center" as const,
        gap: "4px",
        height: "28px",
        padding: "0 12px",
        borderRadius: "999px",
        fontSize: "13px",
        fontWeight: 400,
        cursor: "pointer",
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        backgroundColor: "transparent",
        color: tokens.colorNeutralForeground2,
        userSelect: "none" as const,
        ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    },
    filterChipActive: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
        border: `1px solid ${tokens.colorBrandStroke1}`,
        fontWeight: 600,
    },
    runHistoryCard: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: "4px",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.14), 0px 0px 2px rgba(0,0,0,0.12)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column" as const,
        minHeight: "288px",
    },
    runHistoryCardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 12px",
        flexShrink: 0,
    },
    runHistoryChartBody: {
        padding: "4px 16px 8px 12px",
        height: "240px",
        boxSizing: "border-box" as const,
        overflowX: "auto" as const,
        "& line[stroke='white'], & line[stroke='#fff'], & line[stroke='#ffffff']": {
            display: "none",
        },
    },
    runHistoryChartLegend: {
        display: "flex",
        gap: "16px",
        padding: "4px 16px 12px",
        alignItems: "center" as const,
        justifyContent: "flex-start" as const,
        flexShrink: 0,
    },
    runHistoryHeading: {
        fontFamily: "'Segoe UI', sans-serif",
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "22px",
        color: tokens.colorNeutralForeground1,
    },
    runHistoryLegendItem: {
        display: "flex",
        alignItems: "center" as const,
        gap: "6px",
        fontSize: "11px",
        color: "#666666",
    },
    runHistoryLegendSwatchPassed: {
        width: "10px",
        height: "10px",
        borderRadius: "2px",
        backgroundColor: "#38A652",
    },
    runHistoryLegendSwatchFailed: {
        width: "10px",
        height: "10px",
        borderRadius: "2px",
        backgroundColor: "#D12D2D",
    },
    runHistoryLegendSwatchSkipped: {
        width: "10px",
        height: "10px",
        borderRadius: "2px",
        backgroundColor: "#BABABA",
    },
    runHistoryEmptyBody: {
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 12px 24px",
        gap: "8px",
    },
    runHistoryEmptyIcon: {
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        backgroundColor: "#F0F0F5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "4px",
    },
    runHistoryEmptyTitle: {
        fontSize: "14px",
        fontWeight: 500,
        color: "#333333",
    },
    runHistoryEmptySubtitle: {
        fontSize: "12px",
        lineHeight: "18px",
        color: "#808080",
        textAlign: "center" as const,
    },
    runHistoryLoadingMore: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 16px 10px",
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
    },
    runHistoryLoadingMoreSpinner: {
        display: "inline-block",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        border: `2px solid ${tokens.colorNeutralStroke1}`,
        borderTopColor: tokens.colorBrandForeground1,
        animationName: {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
        },
        animationDuration: "0.8s",
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
    },
    // Status cards
    statusCardsRow: {
        display: "flex",
        gap: "12px",
        flexWrap: "wrap" as const,
    },
    statusCard: {
        flex: "1 1 130px",
        minWidth: "120px",
        padding: "14px 16px",
        borderRadius: "8px",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    statusCardLabel: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
        fontWeight: 500,
    },
    statusCardValue: {
        fontSize: "22px",
        fontWeight: 600,
        color: tokens.colorNeutralForeground1,
    },
    statusCardValuePassed: {
        fontSize: "22px",
        fontWeight: 600,
        color: "#107C10",
    },
    statusCardValueFailed: {
        fontSize: "22px",
        fontWeight: 600,
        color: "#D13438",
    },
    statusCardTrendText: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
    },
    statusCardTrendPositive: {
        fontSize: "11px",
        color: tokens.colorPaletteGreenForeground1,
    },
    statusCardTrendNegative: {
        fontSize: "11px",
        color: tokens.colorPaletteRedForeground1,
    },
    statusCardRunDate: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        marginTop: "2px",
    },
});
